'use client';

import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { cn } from '../../../lib/utils';
import { radioDotVariants, radioVariants, type RadioSize } from './radio-variants';

export type RadioIndicatorProps = Omit<ComponentPropsWithoutRef<'span'>, 'children'> & {
  checked?: boolean;
  disabled?: boolean;
  error?: boolean;
  size?: RadioSize;
};

/**
 * Visual-only radio ring: no role, no focus handling, no click behaviour.
 * Use it inside another interactive control, such as a menu row, where a real
 * Radio would nest a control inside a control.
 */
export const RadioIndicator = forwardRef<HTMLSpanElement, RadioIndicatorProps>(
  (
    { checked = false, disabled = false, error = false, size = 'standard', className, ...rest },
    ref,
  ) => (
    <span
      ref={ref}
      aria-hidden
      data-slot="radio-indicator"
      data-state={checked ? 'checked' : 'unchecked'}
      data-disabled={disabled ? '' : undefined}
      data-error={error ? '' : undefined}
      className={cn(
        radioVariants({ size }),
        'inline-flex items-center justify-center bg-surface-primary',
        'data-[disabled]:border-border-disabled',
        '[&[data-error]:not([data-state=checked]):not([data-disabled])]:border-feedback-error-accent',
        className,
      )}
      {...rest}>
      {checked && <span className={radioDotVariants({ size, disabled })} />}
    </span>
  ),
);
RadioIndicator.displayName = 'RadioIndicator';
